import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GenerateCvService } from 'src/app/pages/generate resume/generate-cv-steps/services/generate-cv/generate-cv.service';
import { PaymentService } from 'src/app/services/payment/payment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { CvDbService } from 'src/app/services/cv-db/cv-db.service';
import { GeneratedcvService } from '../../profile/generatedcv/services/generatedcv.service';

@Component({
  selector: 'app-generate-cv-steps',
  templateUrl: './generate-cv-steps.component.html',
  styleUrls: ['./generate-cv-steps.component.css'],
})
export class GenerateCVStepsComponent {
  steps: MenuItem[] = [];
  activeIndex: number = Number(localStorage.getItem('activeStep')) || 0;

  cvForm!: FormGroup;
  userInfo?: any = JSON.parse(localStorage.getItem('userInfo') || '{}');
  EditMode: any = false;
  generatedCv: any;
  editCvId: any = '';
  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private generateCvService: GenerateCvService,
    private paymentService: PaymentService,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private cvDbService: CvDbService,
    private route: ActivatedRoute,
    private generatedcvService: GeneratedcvService
  ) {
    this.getTranslateSteps();
    this.translate.onLangChange.subscribe(() => {
      this.getTranslateSteps();
    });
    this.editCvId = this.route.snapshot.paramMap.get('cvId');
    const editMode = this.route.snapshot.paramMap.get('editMode');

    this.EditMode = editMode === 'true'; // Convert to boolean
    this.cleanOldFormData();
  }

  async ngOnInit() {
    this.cvForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      title: ['', [Validators.required, Validators.minLength(2)]],
      city: [''],
      phone: ['', [Validators.required, Validators.minLength(8)]],
      portfolio: [''],
      skill: this.fb.array([]),
      languages: this.fb.array([]),
      course: this.fb.array([]),
      target: this.fb.array([]),
      certification: this.fb.array([]),
      project: this.fb.array([]),
      education: this.fb.array([]),
      linkedIn: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/),
        ],
      ],
      country: ['', [Validators.required, Validators.minLength(2)]],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          ),
        ],
      ],
      summary: [''],
      experiences: this.fb.array([]),
      cvTempletePath: ['', Validators.required],
      cvTempleteFile: ['', Validators.required],
      coverLetterPath: ['', Validators.required],
    });

    if (this.editCvId && this.EditMode) {
      // Wait for the API response before loading form data
      try {
        const generatedcvs: any = await this.generatedcvService
          .getGeneratedCV()
          .toPromise();

        this.generatedCv = generatedcvs.find(
          (cv: any) => cv.id == this.editCvId
        );

        if (this.generatedCv && this.generatedCv.generateCvRequest) {
          let formData: any = {
            formValue: this.generatedCv.generateCvRequest,
            editMode: this.EditMode,
            activeStep: 0,
            timestamp: new Date().getTime(),
          };

          localStorage.setItem('cvFormData', JSON.stringify(formData));

          // Now load the form data
          this.loadFormData();
        }
      } catch (error) {
        console.error('Error loading CV for edit:', error);
      }
    } else {
      // Load existing form data if not in edit mode
      this.loadFormData();
    }

    this.cvForm.valueChanges.subscribe(() => {
      this.saveFormData();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.reloadFormDataIfNeeded();
    }, 100);
  }

  private reloadFormDataIfNeeded() {
    const savedData = localStorage.getItem('cvFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (parsedData.formValue) {
          // Check if form is empty but localStorage has data
          const currentName = this.cvForm.get('name')?.value;
          const savedName = parsedData.formValue.name;

          if (!currentName && savedName) {
            this.loadFormData();
          }
        }
      } catch (error) {
        console.error('Error reloading form data:', error);
      }
    }
  }

  private cleanOldFormData() {
    const savedData = localStorage.getItem('cvFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        let needsCleaning = false;

        if (
          parsedData.formValue?.skill &&
          Array.isArray(parsedData.formValue.skill)
        ) {
          const hasObjects = parsedData.formValue.skill.some(
            (item: any) => typeof item === 'object'
          );
          if (hasObjects) {
            parsedData.formValue.skill = parsedData.formValue.skill
              .map((item: any) =>
                typeof item === 'string' ? item : item?.name || ''
              )
              .filter((item: string) => item.trim() !== '');
            needsCleaning = true;
          }
        }

        if (
          parsedData.formValue?.course &&
          Array.isArray(parsedData.formValue.course)
        ) {
          const hasObjects = parsedData.formValue.course.some(
            (item: any) => typeof item === 'object'
          );
          if (hasObjects) {
            parsedData.formValue.course = parsedData.formValue.course
              .map((item: any) =>
                typeof item === 'string' ? item : item?.name || ''
              )
              .filter((item: string) => item.trim() !== '');
            needsCleaning = true;
          }
        }

        if (
          parsedData.formValue?.education &&
          Array.isArray(parsedData.formValue.education)
        ) {
          parsedData.formValue.education = parsedData.formValue.education.map(
            (item: any) => {
              const { startDate, ...rest } = item;
              if (startDate) needsCleaning = true;

              if (rest.endDate && rest.endDate.length === 4) {
                rest.endDate = `${rest.endDate}-01-01`;
                needsCleaning = true;
              }

              return rest;
            }
          );
        }

        if (needsCleaning) {
          localStorage.setItem('cvFormData', JSON.stringify(parsedData));
        }
      } catch (error) {
        localStorage.removeItem('cvFormData');
      }
    }
  }

  private saveFormData() {
    const formValue = { ...this.cvForm.value };

    if (formValue.skill && Array.isArray(formValue.skill)) {
      formValue.skill = formValue.skill
        .map((item: any) =>
          typeof item === 'string' ? item : item?.name || ''
        )
        .filter((item: string) => item && item.trim() !== '');
    }

    if (formValue.course && Array.isArray(formValue.course)) {
      formValue.course = formValue.course
        .map((item: any) =>
          typeof item === 'string' ? item : item?.name || ''
        )
        .filter((item: string) => item && item.trim() !== '');
    }

    const formData = {
      formValue: formValue,
      activeStep: this.activeIndex,
      timestamp: new Date().getTime(),
      editMode: this.EditMode,
    };

    localStorage.setItem('cvFormData', JSON.stringify(formData));
    localStorage.setItem('activeStep', this.activeIndex.toString());
  }

  private loadFormData() {
    const savedData = localStorage.getItem('cvFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);

        if (parsedData.formValue) {
          this.cvForm.patchValue(parsedData.formValue, { emitEvent: false });
          this.restoreFormArrays(parsedData.formValue);
          this.cvForm.updateValueAndValidity();
        }

        if (parsedData.activeStep !== undefined) {
          this.activeIndex = parsedData.activeStep;
        }
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    }
  }

  private restoreFormArrays(savedFormValue: any) {
    const arrayFields = [
      'skill',
      'languages',
      'target',
      'course',
      'certification',
      'project',
      'education',
      'experiences',
    ];

    arrayFields.forEach((fieldName) => {
      if (
        savedFormValue[fieldName] &&
        Array.isArray(savedFormValue[fieldName])
      ) {
        const formArray = this.cvForm.get(fieldName) as FormArray;
        formArray.clear();

        savedFormValue[fieldName].forEach((item: any) => {
          if (fieldName === 'skill' || fieldName === 'course') {
            const value = typeof item === 'string' ? item : item?.name || '';
            if (value.trim() !== '') {
              formArray.push(this.fb.control(value, Validators.required));
            }
          } else {
            const group = this.createFormGroupForArrayItem(fieldName, item);
            if (group) {
              formArray.push(group);
            }
          }
        });
      }
    });
  }

  private createFormGroupForArrayItem(fieldName: string, item: any): any {
    if (!item) return null;

    switch (fieldName) {
      case 'skill':
      case 'course':
        const value = typeof item === 'string' ? item : item?.name || '';
        return this.fb.control(value, Validators.required);

      case 'experiences':
        const expGroup = this.fb.group({
          title: [item.title || ''],
          company: [item.company || ''],
          startDate: [item.startDate || ''],
          endDate: [item.endDate || ''],
          isCurrent: [item.isCurrent || false],
          dutiesAchievements: [item.dutiesAchievements || ''],
        });

        // Setup isCurrent watcher
        expGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
          const endDateControl = expGroup.get('endDate');
          if (isCurrent) {
            endDateControl?.clearValidators();
            endDateControl?.setValue('');
            endDateControl?.disable();
          } else {
            endDateControl?.enable();
            endDateControl?.setValidators([Validators.required]);
          }
          endDateControl?.updateValueAndValidity();
        });

        return expGroup;

      case 'education':
        let endDateValue = item.endDate || '';
        if (endDateValue && endDateValue.length === 4) {
          endDateValue = `${endDateValue}-01-01`;
        }

        const eduGroup = this.fb.group({
          degree: [item.degree || ''],
          major: [
            item.major || '',
            [Validators.required, Validators.minLength(2)],
          ],
          university: [
            item.university || '',
            [Validators.required, Validators.minLength(2)],
          ],
          grade: [item.grade || ''],
          endDate: [endDateValue, Validators.required],
          details: [item.details || ''],
          isCurrent: [item.isCurrent || false],
        });
        eduGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
          const endDateControl = eduGroup.get('endDate');
          if (isCurrent) {
            endDateControl?.clearValidators();
            endDateControl?.setValue('');
          } else {
            endDateControl?.setValidators([Validators.required]);
          }
          endDateControl?.updateValueAndValidity();
        });

        return eduGroup;

      case 'project':
        return this.fb.group({
          name: [item.name || '', Validators.required],
          link: [item.link || ''],
          role: [item.role || ''],
        });

      case 'languages':
        return this.fb.group({
          name: [item.name || '', Validators.required],
          level: [item.level || '', Validators.required],
        });

      case 'certification':
        return this.fb.group({
          name: [item.name || '', Validators.required],
          organization: [item.organization || '', Validators.required],
          date: [item.date || '', Validators.required],
        });

      case 'target':
        return this.fb.group({
          name: [item.name || ''],
          level: [item.level || ''],
        });

      default:
        return this.fb.control(item);
    }
  }

  onStepClick(index: number) {
    if (this.canNavigateToStep(index)) {
      this.activeIndex = index;
      this.saveFormData();

      setTimeout(() => {
        requestAnimationFrame(() => {
          const content = document.querySelector('#app');
          if (content) content.scrollTop = 0;
        });
      }, 50);
    }
  }

  private canNavigateToStep(targetIndex: number): boolean {
    if (targetIndex < this.activeIndex) {
      return true;
    }

    if (targetIndex > this.activeIndex + 1) {
      return false;
    }

    return this.validateCurrentStep();
  }

  private validateCurrentStep(): boolean {
    switch (this.activeIndex) {
      case 0:
        return true;
      case 1:
        return this.cvForm.get('cvTempleteFile')?.valid || false;
      case 2:
        return true;
      case 3:
        return (
          (this.cvForm.get('name')?.valid &&
            this.cvForm.get('email')?.valid &&
            this.cvForm.get('phone')?.valid) ||
          false
        );
      case 4:
        return true;
      case 5:
        const education = this.cvForm.get('education') as FormArray;
        return (
          education.length > 0 && education.controls.some((ctrl) => ctrl.valid)
        );
      case 6:
        return true;
      default:
        return true;
    }
  }

  next() {
    if (this.activeIndex < this.steps.length - 1) {
      this.activeIndex++;
      this.saveFormData();

      setTimeout(() => {
        requestAnimationFrame(() => {
          const content = document.querySelector('#app');
          if (content) content.scrollTop = 0;
        });
      }, 50);
    }
  }

  previous(): any {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.saveFormData();
    }
    setTimeout(() => {
      requestAnimationFrame(() => {
        const content = document.querySelector('#app');
        if (content) content.scrollTop = 0;
      });
    }, 50);
  }

  async DownloadResume() {
    await this.cvDbService.clearAllPdfs();
    let formData: any = localStorage.getItem('cvFormData');
    formData = JSON.parse(formData);

    if (this.editCvId && formData.editMode && formData.editMode == true) {
      /** call edit service */
      this.generateCvService
        .editCv(this.editCvId, this.cvForm.value)
        .subscribe({
          next: (response: any) => {
            this.clearSavedData();
            this.generateCvService.setData(response);
            this.router.navigateByUrl('/cv-review');
          },
          error: (err) => {
            console.error('Error updating CV:', err);
          },
        });
    } else {
      this.generateCvService.sentCvJsonData(this.cvForm.value).subscribe({
        next: (response: any) => {
          this.clearSavedData();
          this.generateCvService.setData(response);

          this.router.navigateByUrl('/cv-review');
        },
        error: (err) => {
          console.error('Error generating CV:', err);
        },
      });
    }
  }

  clearSavedData() {
    localStorage.removeItem('cvFormData');
    localStorage.removeItem('activeStep');
  }

  resetForm() {
    this.cvForm.reset();
    this.activeIndex = 0;
    this.clearSavedData();
  }

  getTranslateSteps() {
    this.translate
      .get([
        'Target',
        'Template',
        'Select Resume',
        'Personal Data',
        'Work Experience',
        'Education',
        'Skills',
        'Review',
      ])
      .subscribe((translations) => {
        this.steps = [
          { label: translations['Target'], command: () => this.onStepClick(0) },
          {
            label: translations['Template'],
            command: () => this.onStepClick(1),
          },
          {
            label: translations['Select Resume'],
            command: () => this.onStepClick(2),
          },
          {
            label: translations['Personal Data'],
            command: () => this.onStepClick(3),
          },
          {
            label: translations['Work Experience'],
            command: () => this.onStepClick(4),
          },
          {
            label: translations['Education'],
            command: () => this.onStepClick(5),
          },
          { label: translations['Skills'], command: () => this.onStepClick(6) },
          { label: translations['Review'], command: () => this.onStepClick(7) },
        ];
      });
  }

  downloadFile(response: any, filename: string, type: string) {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const base64Data = response;
      const binaryData = this.base64ToArrayBuffer(base64Data);
      const blob = new Blob([binaryData], { type });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      link.href = url;
      link.download = filename;
      link.click();

      window.URL.revokeObjectURL(url);
      this.sendGoogleEvent('download cv');
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    let binaryString = '';
    if (typeof window !== 'undefined') {
      binaryString = window.atob(base64);
    }

    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  sendGoogleEvent(name: string) {
    this.googleAnalyticsService.event(name);
    this.gtmService.pushTag({
      event: 'customEvent',
      category: 'button',
      action: 'click',
      label: name,
    });
  }
}
